import { Statement, StatementWithNumber, filterStatements, getStatementNumber, sortStatements } from './waterfall/statement';

/**
 * Statement numbers, as people see them.
 *
 * The number of a statement has always been a rank computed on the fly: the n-th statement of its
 * series, by period, is number n (`sortStatements`, in the frozen model). The engine relies on that
 * rank to find "the previous statement", so it must never change - and it does not, here.
 *
 * A statement may also carry a number of its own, `customNumber`. This file decides which number
 * is shown, on screen, in the PDF and in file names. It lives in the model, outside the frozen
 * waterfall/ folder, so that the app and the PDF function share it:
 * - a statement with a `customNumber` shows it;
 * - any other statement shows its rank, as it always did - unless a chosen number already holds
 *   that rank, in which case it moves on to the next free number. Two statements of one series
 *   can therefore never show the same number.
 *
 * That rank only counts the statements that did not receive their number when they were created
 * (`numberedAtCreation`): the ones that existed before numbers could be chosen, and any statement
 * created elsewhere without one. So creating a statement for an earlier period never pushes the
 * older statements' numbers up, and choosing the number of an older statement never pulls the
 * following ones down - it keeps its place in the count.
 *
 * `customNumber` is a new name on purpose: many statements already carry a stale `number` field,
 * saved by mistake along with a whole numbered statement, and it must not start meaning something.
 */
export type NumberedStatement = Statement & { customNumber?: number, numberedAtCreation?: boolean };

/** A statement numbered by rank, with - where the page computed it - the number to show. */
export type StatementWithDisplayNumber = StatementWithNumber & { displayNumber?: number };

/** The number shown for each statement of one series, keyed by statement id. */
export function displayNumbers(series: NumberedStatement[]): Map<string, number> {
  // sortStatements sorts the array it is given in place: work on a copy, the caller's list keeps its order.
  const chronological = sortStatements([...series], false);
  const taken = new Set<number>();
  const numbers = new Map<string, number>();
  const assign = (id: string, wanted: number) => {
    let number = wanted;
    while (taken.has(number)) number++;
    taken.add(number);
    numbers.set(id, number);
  };

  // Chosen numbers first, so they never move. Should two statements hold the same one (two people
  // saving at the same moment), the later statement by period moves on to the next free number.
  for (const statement of chronological) {
    // Only a whole number of at least 1 counts as chosen; anything else, the statement keeps its rank.
    const chosen = statement.customNumber;
    if (Number.isInteger(chosen) && chosen >= 1) assign(statement.id, chosen);
  }
  // Then every other statement, on its rank among the statements not numbered at creation.
  let rank = 0;
  for (const statement of chronological) {
    if (statement.numberedAtCreation) continue;
    rank++;
    if (!numbers.has(statement.id)) assign(statement.id, rank);
  }
  // A statement flagged as numbered at creation always carries its number; should one ever lack
  // it, it still gets a free number rather than none.
  for (const statement of chronological) {
    if (!numbers.has(statement.id)) assign(statement.id, statement.number);
  }
  return numbers;
}

/**
 * Display counterpart of `getStatementNumber`: the number shown for `current` within its own series,
 * the series being chosen exactly as `getStatementNumber` chooses it.
 */
export function getDisplayNumber(current: Statement, statements: Statement[]) {
  const series = filterStatements(current.type, current.mode, [current.senderId, current.receiverId], current.contractId, statements);
  return displayNumbers(series).get(current.id) ?? getStatementNumber(current, statements);
}

/**
 * The number to print for a statement that went through `sortStatementsWithDisplayNumbers` - or its rank, for the
 * pages that do not compute display numbers (they then show exactly what they showed before).
 */
export function shownNumber(statement: { number?: number, displayNumber?: number }) {
  return statement.displayNumber ?? statement.number;
}

/**
 * A copy of the statement without `displayNumber`, for the flows that save a whole statement taken
 * from a displayed list. The number shown depends on the other statements of the series, so a saved
 * copy of it would go stale - which is exactly how many statements came to carry an outdated `number`.
 */
export function withoutDisplayNumber<T extends object>(statement: T): T {
  const copy = { ...statement };
  delete (copy as { displayNumber?: number }).displayNumber;
  return copy;
}

/**
 * `sortStatements`, with the number to show added to each statement: same order, same `number` - the
 * rank, which other code uses to walk from one statement to the next - and one more field.
 *
 * The display numbers are computed on the list as it was given, before `sortStatements` reorders it:
 * two statements ending on the same day are then ranked exactly as `sortStatements` ranks them.
 * (Computed on its output, which runs from the latest to the earliest, they would swap.)
 */
export function sortStatementsWithDisplayNumbers<T extends NumberedStatement>(series: T[], reverse = true) {
  const numbers = displayNumbers(series);
  return sortStatements(series, reverse).map(statement => ({ ...statement, displayNumber: numbers.get(statement.id) }));
}

/**
 * The number a new statement receives when it is created: the number shown by the statement just
 * before it in its series, by period, plus one - skipping any number the series already shows.
 * 1 for the first statement of a series. With 1, 2, 21, 22, 19, 20, the next one is 23: 20 + 1 = 21,
 * then 22 are taken.
 */
export function nextStatementNumber(created: Statement, statements: Statement[]) {
  const others = statements.filter(s => s.id !== created.id);
  const series = filterStatements(created.type, created.mode, [created.senderId, created.receiverId], created.contractId, [...others, created]);
  const shown = displayNumbers(series.filter(s => s.id !== created.id));

  // `created` is appended last, so on a tie of periods the existing statement counts as the previous one.
  const chronological = sortStatements([...series], false);
  const previous = chronological[chronological.findIndex(s => s.id === created.id) - 1];

  const taken = new Set(shown.values());
  let number = (previous ? shown.get(previous.id) : 0) + 1;
  while (taken.has(number)) number++;
  return number;
}

/**
 * Numbers statements created together, one after the other, as nextStatementNumber does for one: each
 * one also counts the new statements numbered before it, so that two new statements of one series never
 * get the same number. Each new statement needs its id already: numbering tells it apart from the others
 * by its id. `statements` are the ones already stored.
 */
export function numberNewStatements<T extends Statement>(created: T[], statements: Statement[]): (T & NumberedStatement)[] {
  const numbered: (T & NumberedStatement)[] = [];
  for (const statement of created) {
    numbered.push({ ...statement, customNumber: nextStatementNumber(statement, [...statements, ...numbered]), numberedAtCreation: true });
  }
  return numbered;
}

/**
 * The numbers `current` cannot take: the ones the other statements of its series show. Taking one of
 * them would move that other statement off it - and it may be finalised, with a PDF already sent.
 */
export function numbersTakenBesides(current: Statement, statements: Statement[]) {
  const series = filterStatements(current.type, current.mode, [current.senderId, current.receiverId], current.contractId, statements);
  const shown = displayNumbers(series);
  shown.delete(current.id);
  return new Set(shown.values());
}
